import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  PasDateTimeComponent,
  PasFormValidationMessage,
  PasInputFieldComponent,
  PasSelectComponent,
  PasTextareaComponent,
} from '@porsche-aftersales/frontend-toolbox';
import moment from 'moment';

import { TranslatePipe } from '../../../pipes/translate.pipe';
import { translate } from '../../../shared/fn/translate';

import {
  OBJECT_CRITERIA_GROUP_OPTIONS,
  RECOURSE_POSSIBLE_OPTIONS,
  RecourseCriteriaStore,
  toTranslatedKeyValueObject,
  WARRANTY_CALCULATION_METHOD_OPTIONS,
} from './recourse-criteria.store';

const ERRORS: (Omit<PasFormValidationMessage, 'message'> & { messageKey: string })[] = [
  {
    error: 'required',
    messageKey: 'validation.message.pattern',
  },
  {
    error: 'pattern',
    messageKey: 'validation.message.pattern',
  },
  {
    error: 'invalidCommentCharacters',
    messageKey: 'recourseCriteria.commentInvalidCharacters',
  },
];

export const dateAsObject = (date: Date): { year: number; month: number; day: number } => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // Months are 0-indexed, so we add 1
  const day = date.getDate();
  return { year, month, day };
};

@Component({
  selector: 'app-recourse-criteria-editor',
  templateUrl: './recourse-criteria-editor.component.html',
  styleUrls: ['./recourse-criteria-editor.component.scss'],
  imports: [
    CommonModule,
    TranslatePipe,
    ReactiveFormsModule,
    PasInputFieldComponent,
    PasSelectComponent,
    PasTextareaComponent,
    PasDateTimeComponent,
  ],
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecourseCriteriaEditorComponent {
  private readonly recourseCriteriaStore = inject(RecourseCriteriaStore);

  public readonly tomorrow = dateAsObject(moment().clone().add(1, 'days').toDate());
  public readonly longTimeAhead = dateAsObject(new Date(9999, 11, 31));

  public readonly warrantyCalculationMethodOptions = WARRANTY_CALCULATION_METHOD_OPTIONS.map(element =>
    toTranslatedKeyValueObject(element),
  );

  public readonly recoursePossibleOptions = RECOURSE_POSSIBLE_OPTIONS.map(element =>
    toTranslatedKeyValueObject(element),
  );

  public readonly objectCriteriaOptions = OBJECT_CRITERIA_GROUP_OPTIONS.map(element =>
    toTranslatedKeyValueObject(element),
  );

  public readonly validationMessages: PasFormValidationMessage[] = ERRORS.map(error => ({
    ...error,
    message: translate(error.messageKey),
  }));

  public readonly formGroup$ = this.recourseCriteriaStore.formGroup$;
}
